package com.sddamico.components.motion

import androidx.compose.runtime.*
import kotlinx.coroutines.delay

/**
 * Keeps an element mounted for the length of its exit transition.
 *
 * [isMounted] decides whether to compose the element; [isShown] drives its styles. Showing mounts the element in its
 * hidden style and flips [isShown] once that style has been painted, so CSS transitions have a start value. Hiding
 * flips [isShown] straight away and unmounts after [exitMillis]. Both are interruptible: toggling back mid-exit cancels
 * the unmount and the transition reverses from wherever it is.
 */
@Stable
class Presence internal constructor(visible: Boolean) {
    var isMounted by mutableStateOf(visible)
        internal set
    var isShown by mutableStateOf(visible)
        internal set
}

@Composable
fun rememberPresence(visible: Boolean, exitMillis: Int): Presence {
    val presence = remember { Presence(visible) }
    LaunchedEffect(visible) {
        if (visible) {
            if (!presence.isShown) {
                presence.isMounted = true
                awaitPaint()
                presence.isShown = true
            }
        } else {
            presence.isShown = false
            delay(exitMillis.toLong())
            presence.isMounted = false
        }
    }
    return presence
}

/** Suspends until whatever was composed before the call has been rendered at least once. */
suspend fun awaitPaint() {
    withFrameNanos { }
    withFrameNanos { }
}
